import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import { useI18n } from "@/i18n";
import { Sparkles, X, Send, Mic, Loader2, Volume2, VolumeX, Square } from "lucide-react";
import useDragPos from "@/hooks/useDragPos";

const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
const SR_LANG = { it: "it-IT", en: "en-US", fr: "fr-FR", es: "es-ES", de: "de-DE", pt: "pt-PT" };

export default function GeminiAssistant() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const drag = useDragPos("gemini-panel");
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [speak, setSpeak] = useState(() => localStorage.getItem("gem_speak") === "1");
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);
  const endRef = useRef(null);
  const recRef = useRef(null);

  const stopSpeak = () => { audioRef.current?.pause(); audioRef.current = null; setPlaying(null); };

  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("cc-open-gemini", h);
    return () => window.removeEventListener("cc-open-gemini", h);
  }, []);

  const speakText = async (text, idx) => {
    stopSpeak();
    setPlaying(idx);
    try {
      const blob = await api.assistantTts(text.replace(/[*#_`]/g, ""), lang);
      const a = new Audio(URL.createObjectURL(blob));
      audioRef.current = a;
      a.onended = () => setPlaying(null);
      await a.play();
    } catch { setPlaying(null); }
  };

  const toggleSpeak = () => {
    const v = !speak;
    setSpeak(v);
    localStorage.setItem("gem_speak", v ? "1" : "0");
    if (!v) stopSpeak();
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy, open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    const hist = msgs.slice(-8).map(({ role, content }) => ({ role, content }));
    setMsgs((m) => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const res = await api.assistantChat({ message: q, history: hist, lang });
      let replyIdx = 0;
      setMsgs((m) => { replyIdx = m.length; return [...m, { role: "assistant", content: res.reply }]; });
      if (speak) speakText(res.reply, replyIdx);
      (res.actions || []).forEach((a) => { if (a.type === "navigate" && a.path) nav(a.path); });
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: t("gemError") }]);
    } finally { setBusy(false); }
  };

  const toggleMic = () => {
    if (!SR) return;
    if (listening) { recRef.current?.stop(); return; }
    const r = new SR();
    recRef.current = r;
    r.lang = SR_LANG[lang] || "it-IT";
    r.interimResults = false;
    r.onresult = (ev) => { setListening(false); send(ev.results[0][0].transcript); };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    setListening(true);
    r.start();
  };

  const suggestions = [t("gemS1"), t("gemS2"), t("gemS3"), t("gemS4")];
  const quick = [
    { label: t("gemQuickOrders"), text: t("gemQuickOrdersTxt") },
    { label: t("gemQuickRevenue"), text: t("gemQuickRevenueTxt") },
    { label: t("gemQuickLow"), text: t("gemQuickLowTxt") },
    { label: t("gemQuickRecipe"), fill: t("gemQuickRecipeTxt") },
    { label: t("gemQuickOrder"), fill: t("gemQuickOrderTxt") },
  ];

  return (
    <>
      {open && (
        <div ref={drag.ref} style={window.innerWidth >= 640 ? drag.style : undefined}
          className="fixed inset-0 sm:inset-auto sm:bottom-4 sm:right-4 z-[130] sm:w-[400px] sm:h-[600px] sm:max-h-[82vh] bg-card sm:rounded-2xl shadow-2xl flex flex-col sm:border sm:border-border" data-testid="gemini-panel">
          <div onPointerDown={(e) => { if (window.innerWidth >= 640) drag.start(e); }} style={{ touchAction: "none" }}
            className="flex items-center gap-2 px-4 py-3 border-b border-border bg-gradient-to-r from-[#1a73e8] to-[#7b2ff7] text-white sm:rounded-t-2xl safe-top sm:cursor-move select-none">
            <Sparkles className="w-5 h-5" />
            <div className="flex-1">
              <div className="font-bold text-sm">{t("gemTitle")}</div>
              <div className="text-[11px] opacity-85">{t("gemSub")}</div>
            </div>
            <button onClick={toggleSpeak} title={speak ? t("gemVoiceOff") : t("gemVoiceOn")} className="p-1.5 rounded-lg hover:bg-white/15" data-testid="gemini-voice-toggle">
              {speak ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 opacity-70" />}
            </button>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/15" data-testid="gemini-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-2.5">
            {msgs.length === 0 && (
              <div className="mt-2">
                <p className="text-sm text-muted-foreground mb-3">{t("gemHint")}</p>
                <div className="flex flex-col gap-2">
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => send(s)} className="text-left text-sm border border-border rounded-xl px-3 py-2 hover:bg-muted transition-colors" data-testid={`gemini-sugg-${i}`}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} data-testid={`gemini-msg-${i}`}
                className={`max-w-[88%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "self-end bg-[#0d1f3d] text-white rounded-br-sm" : "self-start bg-muted rounded-bl-sm"}`}>
                {m.content}
                {m.role === "assistant" && (
                  <button onClick={() => (playing === i ? stopSpeak() : speakText(m.content, i))}
                    className="block mt-1.5 text-muted-foreground hover:text-foreground transition-colors" data-testid={`gemini-play-${i}`}>
                    {playing === i ? <Square className="w-3.5 h-3.5 fill-current" /> : <Volume2 className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
            ))}
            {busy && <div className="self-start flex items-center gap-2 text-sm text-muted-foreground px-1" data-testid="gemini-thinking"><Loader2 className="w-4 h-4 animate-spin" /> {t("gemThinking")}</div>}
            <div ref={endRef} />
          </div>
          <div className="flex gap-1.5 overflow-x-auto px-3 pt-2 pb-1 whitespace-nowrap border-t border-border" data-testid="gemini-quick-bar">
            {quick.map((q, i) => (
              <button key={i} onClick={() => (q.fill ? setInput(q.fill) : send(q.text))} disabled={busy}
                className="shrink-0 text-[11px] font-medium bg-muted hover:bg-muted/70 rounded-full px-3 py-1.5 transition-colors disabled:opacity-50" data-testid={`gemini-quick-${i}`}>
                {q.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 px-3 py-2.5 border-t border-border safe-bottom">
            {SR && (
              <button onClick={toggleMic} data-testid="gemini-mic"
                className={`p-2.5 rounded-full transition-colors ${listening ? "bg-red-500 text-white animate-pulse" : "bg-muted text-foreground hover:bg-muted/70"}`}>
                <Mic className="w-4 h-4" />
              </button>
            )}
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={t("gemPh")} className="flex-1 bg-muted rounded-full px-4 py-2.5 text-sm outline-none" data-testid="gemini-input" />
            <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="gemini-send"
              className="p-2.5 rounded-full bg-gradient-to-r from-[#1a73e8] to-[#7b2ff7] text-white disabled:opacity-40">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
